import {
  computeAlSi,
  computeStabilityIndex,
} from '../../computations/labComputations'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import {
  getIsoYears,
  getTestLimit,
  isBioFuel,
  isDistillateKind,
  ISO_NOT_AVAILABLE,
  isOtherFuel,
  isResidualKind,
  recalculateIsoSelection,
} from './laboratoryRules'
import { Input, Select } from 'antd'
import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import RequiredLabel from 'components/RequiredLabel'
import { sanitizeNumericInput } from '../../../../utils/sanitizeNumericInput'
import { useTranslation } from 'react-i18next'

import './labTestSection.less'

const TEST_DEFS = [
  { key: 'density15', method: 'ISO 12185', unit: 'kg/m³' },
  { key: 'viscosity50', method: 'ISO 3104', unit: 'mm²/s' },
  { key: 'viscosity40', method: 'ISO 3104', unit: 'mm²/s' },
  { key: 'flashpoint', method: 'ISO 2719-E', unit: '°C' },
  { key: 'pourpoint', method: 'ISO 3016', unit: '°C' },
  { key: 'appearance', method: 'Visual', unit: '' },
  { key: 'cetaneIndex', method: 'Calculated', unit: '' },
  { key: 'lubricity', method: 'ISO 12156-1', unit: 'µm' },
  { key: 'mcrt10pctResidue', method: 'ISO 10370', unit: 'mass %' },
  { key: 'microCarbonResidue', method: 'ISO 10370', unit: 'mass %' },
  { key: 'ashContent', method: 'ISO 6245', unit: 'mass %' },
  { key: 'waterContent', method: 'ISO 3733', unit: 'vol %' },
  { key: 'sulphurContent', method: 'ISO 8754', unit: 'mass %' },
  { key: 'totalSedAccelerated', method: 'ISO 10307-1', unit: 'mass %' },
  { key: 'totalSedPotential', method: 'ISO 10307-2', unit: 'mass %' },
  { key: 'aluminium', method: 'IP 501', unit: 'mg/kg' },
  { key: 'silicon', method: 'IP 501', unit: 'mg/kg' },
  { key: 'sodium', method: 'IP 501', unit: 'mg/kg' },
  { key: 'vanadium', method: 'IP 501', unit: 'mg/kg' },
  { key: 'nickel', method: 'IP 501', unit: 'mg/kg' },
  { key: 'iron', method: 'IP 501', unit: 'mg/kg' },
  { key: 'calcium', method: 'IP 501', unit: 'mg/kg' },
  { key: 'zinc', method: 'IP 501', unit: 'mg/kg' },
  { key: 'phosphorous', method: 'IP 501', unit: 'mg/kg' },
  { key: 'magnesium', method: 'IP 501', unit: 'mg/kg' },
  { key: 'lead', method: 'IP 501', unit: 'mg/kg' },
  { key: 'potassium', method: 'IP 501', unit: 'mg/kg' },
  { key: 'fame', method: 'ASTM D7371', unit: '%' },
  { key: 'lowHeatingValue', method: 'tba', unit: 'MJ/kg' },
  { key: 'netCalorificValue', method: 'Calculated', unit: 'MJ/kg' },
  { key: 'ccai', method: 'Calculated', unit: '' },
  // eslint-disable-next-line sort-keys
  {
    calculated: true,
    key: 'aluminiumSilicon',
    method: 'Calculated',
    unit: 'mg/kg',
  },
  { key: 'totalSedExistent', method: 'ISO 10307-1', unit: 'mass %' },
  // eslint-disable-next-line sort-keys
  {
    calculated: true,
    key: 'stabilityIndex',
    method: 'Calculated',
    unit: 'mass %',
  },
  { key: 'totalAcidNumber', method: 'ASTM D664', unit: 'mg KOH/g' },
]

const DISTILLATE_ONLY_KEYS = [
  'appearance',
  'cetaneIndex',
  'lubricity',
  'mcrt10pctResidue',
  'viscosity40',
]

const RESIDUAL_ONLY_KEYS = [
  'microCarbonResidue',
  'totalSedAccelerated',
  'totalSedExistent',
  'totalSedPotential',
  'viscosity50',
]

const BIO_ONLY_KEYS = ['fame', 'lowHeatingValue']

const field = key => `laboratory.tests.${key}`

// ─── Individual result cell ────────────────────────────────────────────────────

const ResultCell = ({
  calculated,
  calculatedValue,
  control,
  disabled,
  showErrors,
  testKey,
}) => {
  if (calculated) {
    return (
      <span className="lab-test-row__result lab-test-row__result--calc">
        {calculatedValue ?? '\u2014'}
      </span>
    )
  }

  return (
    <Controller
      control={control}
      name={field(testKey)}
      render={({ field: f, fieldState }) => {
        const errorMessage = showErrors ? fieldState.error?.message : undefined

        if (testKey === 'appearance') {
          return (
            <div className="lab-test-row__field-stack">
              <Select
                className="lab-test-row__result-input"
                disabled={disabled}
                onChange={f.onChange}
                placeholder="Select"
                status={errorMessage ? 'error' : undefined}
                value={f.value || undefined}
                options={[
                  { label: 'Clear', value: 'Clear' },
                  { label: 'Hazy', value: 'Hazy' },
                ]}
              />
              {errorMessage && (
                <span className="lab-test-row__field-error">
                  {errorMessage}
                </span>
              )}
            </div>
          )
        }

        return (
          <div className="lab-test-row__field-stack">
            <Input
              {...f}
              aria-invalid={errorMessage ? 'true' : 'false'}
              className="lab-test-row__result-input"
              disabled={disabled}
              inputMode="decimal"
              placeholder=""
              status={errorMessage ? 'error' : undefined}
              onChange={event =>
                f.onChange(sanitizeNumericInput(event.target.value))
              }
            />
            {errorMessage && (
              <span className="lab-test-row__field-error">{errorMessage}</span>
            )}
          </div>
        )
      }}
    />
  )
}

ResultCell.propTypes = {
  calculated: PropTypes.bool,
  calculatedValue: PropTypes.string,
  control: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  showErrors: PropTypes.bool,
  testKey: PropTypes.string,
}

// ─── Main component ────────────────────────────────────────────────────────────

const LabTestSection = ({ saveAttempted }) => {
  const { t } = useTranslation()
  const {
    control,
    formState: { submitCount },
    setValue,
    trigger,
  } = useFormContext()

  const kind = useWatch({ control, name: 'bunker.bargeKind' })
  const fuelGrade = useWatch({ control, name: 'bunker.fuelGradePerBdn' })
  const isoYear = useWatch({ control, name: 'laboratory.isoYear' })

  // Calculated: Aluminium + Silicon
  const alVal = useWatch({ control, name: field('aluminium') })
  const siVal = useWatch({ control, name: field('silicon') })
  const alSiCalc = computeAlSi(alVal, siVal)

  // Calculated: Stability Index = TSP − TSE, fallback TSA − TSE
  const tsaVal = useWatch({ control, name: field('totalSedAccelerated') })
  const tspVal = useWatch({ control, name: field('totalSedPotential') })
  const tseVal = useWatch({ control, name: field('totalSedExistent') })
  const stabilityCalc = computeStabilityIndex(tspVal, tseVal, tsaVal)

  const isoYears = useMemo(
    () => getIsoYears(kind, fuelGrade),
    [kind, fuelGrade]
  )
  const isoOptions = useMemo(
    () => isoYears.map(year => ({ label: year, value: year })),
    [isoYears]
  )

  const isoSelection = useMemo(
    () =>
      recalculateIsoSelection({
        currentIso: isoYear,
        fuelGrade,
        kind,
      }),
    [fuelGrade, isoYear, kind]
  )

  const otherFuel = isOtherFuel(fuelGrade)
  const bioFuel = isBioFuel(fuelGrade)
  const distillateKind = isDistillateKind(kind)
  const residualKind = isResidualKind(kind)
  const isoDisabled = isoSelection.isoDisabled
  const testsLocked =
    otherFuel || !isoYear || isoYear === ISO_NOT_AVAILABLE || isoDisabled

  useEffect(() => {
    const nextIso = isoSelection.value

    if (nextIso !== isoYear) {
      setValue('laboratory.isoYear', nextIso, { shouldValidate: true })
    }
  }, [isoSelection.value, isoYear, setValue])

  useEffect(() => {
    if (!bioFuel) {
      setValue(field('fame'), '', { shouldValidate: true })
      setValue(field('lowHeatingValue'), '', { shouldValidate: true })
    }
  }, [bioFuel, setValue])

  useEffect(() => {
    setValue(field('aluminiumSilicon'), alSiCalc === '\u2014' ? '' : alSiCalc)
    setValue(
      field('stabilityIndex'),
      stabilityCalc === '\u2014' ? '' : stabilityCalc
    )
  }, [alSiCalc, setValue, stabilityCalc])

  useEffect(() => {
    trigger('laboratory')
  }, [bioFuel, distillateKind, residualKind, testsLocked, trigger])

  const calcValues = {
    aluminiumSilicon: alSiCalc,
    stabilityIndex: stabilityCalc,
  }

  const resolvedIsoOptions =
    isoYear === ISO_NOT_AVAILABLE
      ? [{ label: ISO_NOT_AVAILABLE, value: ISO_NOT_AVAILABLE }, ...isoOptions]
      : isoOptions

  const showErrors = saveAttempted || submitCount > 0

  // Hide rows that structurally don't apply to the selected kind/fuel type.
  // These are never shown regardless of ISO year selection.
  const isTestHidden = testKey => {
    if (BIO_ONLY_KEYS.includes(testKey)) return !bioFuel
    if (DISTILLATE_ONLY_KEYS.includes(testKey)) return !distillateKind
    if (RESIDUAL_ONLY_KEYS.includes(testKey)) return !residualKind

    return false
  }

  // Disable result inputs when no valid ISO year is selected.
  const isTestDisabled = () => testsLocked

  return (
    <div className="lab-card">
      {/* 5-column header */}
      <div className="lab-card__header lab-test-header">
        <span className="lab-test-header__test">
          {t('rob.survey.bunker.lab.tests.headerTest')}
        </span>
        <span className="lab-test-header__unit">
          {t('rob.survey.bunker.lab.tests.headerUnit')}
        </span>
        <span className="lab-test-header__method">
          {t('rob.survey.bunker.lab.tests.headerMethod')}
        </span>
        <span className="lab-test-header__result">
          {t('rob.survey.bunker.lab.tests.headerResult')}
        </span>
        <span className="lab-test-header__iso">
          {t('rob.survey.bunker.lab.tests.headerIso')}
        </span>
      </div>

      <div className="lab-card__body">
        {/* ISO year selector row */}
        <div className="lab-test-row lab-test-row--iso-year">
          <span className="lab-test-row__name lab-form-row__label--teal">
            {t('rob.survey.bunker.lab.tests.isoYear')}
          </span>
          <span className="lab-test-row__unit" />
          <span className="lab-test-row__method" />
          <span className="lab-test-row__result-input" />
          <Controller
            control={control}
            name="laboratory.isoYear"
            render={({ field: { onChange, value }, fieldState }) => (
              <div className="lab-test-row__field-stack">
                <Select
                  className="lab-test-row__iso-select"
                  disabled={isoDisabled}
                  onChange={onChange}
                  options={resolvedIsoOptions}
                  placeholder="Select year"
                  showSearch
                  status={showErrors && fieldState.error ? 'error' : undefined}
                  value={value || undefined}
                />
                {showErrors && fieldState.error?.message && (
                  <span className="lab-test-row__field-error">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Test rows — structurally inapplicable rows are hidden*/}
        {TEST_DEFS.filter(({ key }) => !isTestHidden(key)).map(
          ({ calculated, key, method, unit }) => (
            <div
              className={`lab-test-row${calculated ? ' lab-test-row--calculated' : ''}`}
              key={key}
            >
              <span className="lab-test-row__name lab-form-row__label--teal">
                <RequiredLabel
                  required={!calculated}
                  text={t(`rob.survey.bunker.lab.tests.${key}`)}
                />
              </span>
              <span className="lab-test-row__unit">{unit}</span>
              <span className="lab-test-row__method">{method}</span>
              <ResultCell
                calculated={calculated}
                calculatedValue={calcValues[key]}
                control={control}
                disabled={isTestDisabled()}
                showErrors={showErrors}
                testKey={key}
              />
              <span className="lab-test-row__iso-value">
                {getTestLimit(kind, fuelGrade, isoYear, key)}
              </span>
            </div>
          )
        )}
      </div>
    </div>
  )
}

LabTestSection.propTypes = {
  saveAttempted: PropTypes.bool,
}

LabTestSection.defaultProps = {
  saveAttempted: false,
}

export default LabTestSection
